import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SecurityContext,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { UserOperation } from 'src/app/core/enums/user-operation';
import { ShiveGridWrapperComponent } from '../../../../../../shared/components/shive-controls/shive-grid/shive-grid-wrapper.component';
import { ShiveDialogService } from '../../../../../../core/services/controls/shive-dialog.service';
import {
  BudgetOptions,
  ExpenditureOptions,
} from '../../../../../../core/constants-repository/project-calculation.constants';
import { SubSink } from 'subsink';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { ProjectMemberService } from '../../services/project-member.service';
import { ProjectDataService } from '../../../state-and-data-handling/project-data.service';
import { filter } from 'rxjs/operators';
import { ProjectExpenditureBudgetTransformerService } from '../project-calculation-form/services/project-expenditure-budget-transformer.service';
import { faCircleMinus, faPen } from '@fortawesome/pro-light-svg-icons';
import { ProjectMemberGridService } from './project-member-grid.service';
import { cloneDeep } from 'lodash-es';
import { ProjectMemberDialogComponent } from '../../dialogs/project-member-dialog/project-member-dialog.component';
import { ProjectModel } from '../../../../../../core/models/project/project.model';
import { ProjectMemberGridRow } from '../../../../shared/project-detail-types';
import { TimeFormatType } from '../../../../../../core/pipes/time-format.pipe';
import { ExpenditureRateEntry } from '../../../../../../core/models/project/project-expenditure-entry.model';

@Component({
  selector: 'app-project-member-grid',
  templateUrl: './project-member-grid.component.html',
  standalone: false,
})
export class ProjectMemberGridComponent implements OnChanges, AfterViewInit, OnDestroy, OnInit {
  @Input() set selectedExpenditureRateOption(option: ExpenditureOptions) {
    this._selectedExpenditureRateOption.set(option);
  }
  @Input() set selectedBudgetOption(option: BudgetOptions) {
    this._selectedBudgetOption.set(option);
  }
  @Output() readonly assigneeRemovedEmitter$ = new EventEmitter();

  // Derived UI state (computed signals)
  readonly showHourlyExpenditureRateFormField = computed(
    () => this._selectedExpenditureRateOption() === ExpenditureOptions.HourlyRatePerMember,
  );

  readonly showBudgetHoursPerMemberFormField = computed(
    () => this._selectedBudgetOption() === BudgetOptions.HoursPerMember,
  );

  @ViewChild('gridWrapperComponent') gridWrapperComponent: ShiveGridWrapperComponent;
  public formGroup: FormGroup;
  public expenditureValueColumnTitle = 'Stundensatz';
  public hoursPerMemberColumnTitle = 'Budget';
  public readonly UserOperation = UserOperation;
  public readonly SecurityContext = SecurityContext;
  public readonly faPenToSquare = faPenToSquare;
  public readonly TimeFormatType = TimeFormatType;

  // DI
  public readonly projectMemberGridService = inject(ProjectMemberGridService);
  private readonly shiveDialogService = inject(ShiveDialogService);
  private readonly projectMemberService = inject(ProjectMemberService);
  private readonly projectDataService = inject(ProjectDataService);
  private readonly projectExpenditureBudgetService = inject(
    ProjectExpenditureBudgetTransformerService,
  );

  // Inputs -> internal signals
  private readonly _selectedExpenditureRateOption = signal<ExpenditureOptions | null>(null);
  private readonly _selectedBudgetOption = signal<BudgetOptions | null>(null);

  private readonly subs = new SubSink();

  // Getter / Setter
  public get gridData(): ProjectMemberGridRow[] {
    return this.projectMemberGridService.gridData();
  }

  private get project(): ProjectModel {
    return this.projectDataService.getProjectClone();
  }

  ngOnInit(): void {
    const project = this.project;

    // Initialize computed signals
    if (!this._selectedExpenditureRateOption()) {
      this._selectedExpenditureRateOption.set(project.expenditureType);
    }
    if (!this._selectedBudgetOption()) {
      this._selectedBudgetOption.set(project.budgetType);
    }

    project.projectMemberPropertiesWrapper.forEach((projectMemberWrapper) => {
      this.gridData.push({
        member: projectMemberWrapper.member,
        budgetSeconds: 0,
        expenditureRateHistory: [],
        deductCostsFromBudget: projectMemberWrapper.deductCostsFromBudget,
      });
    });

    this.projectExpenditureBudgetService.transformExpenditureAndBudgetEntriesToMemberGridData(
      this.gridData,
      project,
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If values are initialized (firstChange === true), return.
    if (
      changes.selectedExpenditureRateOption?.firstChange ||
      changes.selectedBudgetOption?.firstChange
    ) {
      return;
    }

    const project = this.project;

    if (
      changes.selectedExpenditureRateOption !== undefined &&
      changes.selectedExpenditureRateOption.currentValue === ExpenditureOptions.HourlyRatePerMember
    ) {
      this.projectMemberService.resetExpenditure(this.gridData, project.startsAt);
      this.projectMemberService.syncProjectModel(this.gridData, project);
    }

    if (
      changes.selectedBudgetOption !== undefined &&
      changes.selectedBudgetOption.currentValue === BudgetOptions.HoursPerMember
    ) {
      this.projectMemberService.resetBudget(this.gridData);
      this.projectMemberService.syncProjectModel(this.gridData, project);
    }
  }

  ngAfterViewInit(): void {
    this.initContextMenuCallbacks();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // Apply result of updated history rate entries edited in grid.
  public readonly makeApplyResult =
    (row: ProjectMemberGridRow) => (entries: ExpenditureRateEntry[]) => {
      this.projectMemberService.setHistoryForMember(this.gridData, row.member.id, entries);
      this.projectMemberService.syncProjectModel(this.gridData, this.project);
      this.projectDataService.userOperationConducted$.next(true);
    };

  public openMemberDialog(
    userOperation: UserOperation,
    memberGridRow?: ProjectMemberGridRow,
  ): void {
    let initialRow = cloneDeep(memberGridRow);
    let title = 'Projektmitglied bearbeiten';

    if (userOperation === UserOperation.Create) {
      initialRow = this.projectMemberGridService.createEmptyMemberGridDRow();
      title = 'Projektmitglied hinzufügen';
    }

    // Todo: the projectMemberService is wrong here; this should load all users in an account
    // Todo: load members in dialog
    const dialogRef = this.shiveDialogService.open({
      title,
      content: ProjectMemberDialogComponent,
      width: 572,
    });
    const dialogCmp = dialogRef.content.instance as ProjectMemberDialogComponent;

    // Set dialog inputs (they are signals in the dialog)
    const projectMembers = this.project.projectMemberPropertiesWrapper.map(
      (projectMemberWrapper) => projectMemberWrapper.member,
    );
    dialogCmp.membersInGrid.set([...projectMembers]);
    dialogCmp.memberGridRow.set(initialRow);
    dialogCmp.userOperation.set(userOperation);
    dialogCmp.rateAndBudget.set(this.getActiveColumn(initialRow));
    dialogCmp.showHourlyExpenditureRateFormField.set(this.showHourlyExpenditureRateFormField());
    dialogCmp.showBudgetHoursPerMemberFormField.set(this.showBudgetHoursPerMemberFormField());

    dialogRef.result
      .pipe(filter((result) => result !== UserOperation.Cancel))
      .subscribe((result: ProjectMemberGridRow[]) => {
        this.updateGrid(result);
        this.makeApplyResult(result[0])(result[0].expenditureRateHistory);
      });
  }

  public updateGrid(memberGridRow: ProjectMemberGridRow[]): void {
    memberGridRow.forEach((memberGridRow) => {
      const idx = this.gridData.findIndex((row) => row.member.id === memberGridRow.member.id);

      if (idx < 0) {
        this.gridData.push(memberGridRow);
        return;
      }

      const updated: ProjectMemberGridRow = {
        ...memberGridRow,
      };

      this.gridData.splice(idx, 1, updated);
    });
  }

  private initContextMenuCallbacks(): void {
    this.gridWrapperComponent.contextMenuItems = [
      {
        text: 'Bearbeiten',
        faIcon: faPen,
        callback: (row: ProjectMemberGridRow) => this.openMemberDialog(UserOperation.Update, row),
        isVisible: true,
        isActivated: true,
      },
      {
        text: 'Entfernen',
        faIcon: faCircleMinus,
        callback: (memberGridRow: ProjectMemberGridRow) => {
          this.projectMemberGridService.removeMember(memberGridRow);
          this.assigneeRemovedEmitter$.next(memberGridRow.member.id);
        },
        isVisible: true,
        isActivated: true,
      },
    ];
  }

  private getActiveColumn(row: ProjectMemberGridRow) {
    let expenditureRate = 0;
    let budget = 0;

    if (this.showHourlyExpenditureRateFormField()) {
      const hist = row.expenditureRateHistory ?? [];
      expenditureRate = hist[hist.length - 1]?.rate ?? 0;
    }
    if (this.showBudgetHoursPerMemberFormField()) {
      budget = row.budgetSeconds ?? 0;
    }

    return [expenditureRate, budget];
  }
}
